import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatFieldValueKind, SyncTriggerKind } from '@insio/shared-types';
import { PasswordInput } from '@insio/ui-toolkit';
import { fieldLabelOf, localizedTextOf, useEntityLabels } from '@/features/integrations';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { stashProposalSecrets } from '../../proposal-secrets';
import { CollapsibleRows } from './CollapsibleRows';
import { ProposalShell } from './ProposalShell';
import type {
  ChatChangePayload,
  ChatFieldChangeRow,
  ChatFieldValues,
  ChatSecretField
} from './chat-proposal.types';
import { translatedOr } from './translated-or';

const ENUM_VALUE = /^[A-Z][A-Z0-9_]*$/;

interface Props {
  title: string;
  proposalId: string;
  payload: ChatChangePayload;
  /** Secrets the card's form asks for; their values go with the confirm only. */
  secretFields?: readonly ChatSecretField[];
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/** Confirmation card for a create or edit: every field it changes, before → after. */
export function ChatChangeProposalCard({
  title,
  proposalId,
  payload,
  secretFields = [],
  resolved,
  onConfirm,
  onCancel
}: Props) {
  const t = useFeatureTranslation('aiChat');
  const { i18n } = useTranslation();
  const tDirection = useEnumTranslation('syncDirection');
  const entityLabels = useEntityLabels();
  // Held in this card only — never in the message parts that are saved as chat history.
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const empty = t('proposals.chat.empty');
  // A coded value (an enum) shows its label when the field has one.
  const enumValueOf = (field: string, value: string) =>
    ENUM_VALUE.test(value)
      ? translatedOr(t, `proposals.chat.values.${field}.${value}`, value)
      : value;
  const description = translatedOr(
    t,
    payload.targetKey && `proposals.chat.targets.${payload.targetKey}`,
    payload.target,
    payload.targetParams
  );
  /** One typed value in the UI language; an unknown one keeps its raw text. */
  const translateValue = (kind: ChatFieldValueKind, value: string): string => {
    switch (kind) {
      case ChatFieldValueKind.ENTITY_TYPE:
        return entityLabels.label(value);
      case ChatFieldValueKind.SYNC_DIRECTION:
        return tDirection(value);
      case ChatFieldValueKind.YES_NO:
        return t(value === 'true' ? 'proposals.chat.values.yes' : 'proposals.chat.values.no');
      case ChatFieldValueKind.SCHEDULE:
        return scheduleLabel(value);
    }
  };
  /** A schedule entry `mode|kind|…` as a sentence; the raw text for a shape it does not know. */
  const scheduleLabel = (value: string): string => {
    const [mode, kind, first, second] = value.split('|');
    const modeText = translatedOr(t, `proposals.chat.values.syncMode.${mode}`, mode);
    switch (kind) {
      case SyncTriggerKind.CRON:
        return t(
          second
            ? 'proposals.chat.values.schedule.cronInZone'
            : 'proposals.chat.values.schedule.cron',
          { mode: modeText, expr: first, timezone: second }
        );
      case SyncTriggerKind.INTERVAL:
        return t('proposals.chat.values.schedule.interval', {
          mode: modeText,
          count: Number(first)
        });
      case SyncTriggerKind.EVENT:
        return t('proposals.chat.values.schedule.event', { mode: modeText });
      default:
        return value;
    }
  };
  /** One side of a row in the UI language; its plain text when no translation fits. */
  const sideOf = (
    field: string,
    text: string | null,
    values: ChatFieldValues | null,
    optionLabel: Record<string, string>
  ): string | null => {
    const option = localizedTextOf(optionLabel, i18n.resolvedLanguage);
    if (option !== undefined) return option;
    if (values === null) return text === null ? null : enumValueOf(field, text);
    return values.values.map(value => translateValue(values.kind, value)).join(', ');
  };
  const valueOf = (change: ChatFieldChangeRow) =>
    sideOf(change.field, change.to, change.toValues, change.toLabel);
  const previousOf = (change: ChatFieldChangeRow) =>
    sideOf(change.field, change.from, change.fromValues, change.fromLabel);
  const missingRequired = secretFields.some(
    field => field.required && (secrets[field.key] ?? '') === ''
  );

  const confirm = (id: string) => {
    if (secretFields.length > 0) {
      stashProposalSecrets(
        id,
        secretFields.flatMap(field => {
          const value = secrets[field.key] ?? '';
          return value === '' ? [] : [{ key: field.key, value }];
        })
      );
      setSecrets({});
    }
    onConfirm(id);
  };

  return (
    <ProposalShell
      title={title}
      description={description}
      proposalId={proposalId}
      resolved={resolved}
      onConfirm={confirm}
      onCancel={onCancel}
      confirmDisabled={missingRequired}
    >
      <CollapsibleRows
        rows={payload.changes}
        container={children => (
          <dl className='m-0 p-0 flex flex-col gap-2xs max-h-64 overflow-y-auto'>{children}</dl>
        )}
        renderRow={change => (
          <div
            // A field may repeat (one row per invoice or member); its values keep the key unique.
            key={`${change.field}:${change.from ?? ''}:${change.to ?? ''}`}
            className='flex flex-col gap-2xs border-b border-border-base py-2xs last:border-b-0'
          >
            <dt className='text-foreground-subtle text-xs'>
              {change.label !== null
                ? // A plugin setting: its own label, as on the Integrations form.
                  fieldLabelOf(change.field, { label: change.label }, i18n.resolvedLanguage)
                : translatedOr(t, `proposals.chat.fields.${change.field}`, change.field)}
            </dt>
            <dd className='m-0 text-sm text-foreground-base break-words'>
              {change.from !== null && (
                <span className='line-through text-foreground-subtle'>{previousOf(change)}</span>
              )}
              {change.from !== null && ' → '}
              <span className='font-medium'>{valueOf(change) ?? empty}</span>
            </dd>
          </div>
        )}
      />
      {resolved === undefined && secretFields.length > 0 && (
        <div className='flex flex-col gap-sm'>
          <span className='text-foreground-muted text-xs'>
            {t('proposals.chat.secretForm.hint')}
          </span>
          {secretFields.map(field => (
            <label key={field.key} className='flex flex-col gap-2xs text-sm text-foreground-base'>
              <span>
                {/* The plugin's own label, as on the Integrations form; else a readable key. */}
                {fieldLabelOf(field.key, { label: field.label }, i18n.resolvedLanguage)}
                {field.required && ' *'}
              </span>
              <PasswordInput
                autoComplete='new-password'
                value={secrets[field.key] ?? ''}
                onChange={event =>
                  setSecrets(current => ({ ...current, [field.key]: event.target.value }))
                }
                ariaLabelShow={t('proposals.chat.secretForm.show')}
                ariaLabelHide={t('proposals.chat.secretForm.hide')}
              />
            </label>
          ))}
        </div>
      )}
    </ProposalShell>
  );
}
